// ============================================================================
// BLOCK SERVICE
// ----------------------------------------------------------------------------
// A block is one-way and private to the person who set it. Stored server-side
// at users/{uid}/blocks/{targetUid} (written via the API so the target is
// validated). Effects:
//   voice/video : blocked player is auto-muted and their video hidden for you
//   table chat  : their messages are hidden for you
//   DMs         : neither side can open or message a conversation
//                 (Firestore rules check both directions on every write)
// ============================================================================
import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import gameApiService from './gameApiService';

export interface BlockedPlayer {
  targetUserId: string;
  username: string;
  photoURL?: string | null;
  createdAt?: Date;
}

export const subscribeToBlocks = (
  uid: string,
  callback: (blocks: BlockedPlayer[]) => void,
): (() => void) => {
  if (!uid) return () => {};
  const ref = collection(db, 'users', uid, 'blocks');
  return onSnapshot(
    ref,
    (snap) => {
      callback(snap.docs.map((d) => {
        const x: any = d.data();
        return {
          targetUserId: d.id,
          username: x.username || 'Player',
          photoURL: x.photoURL || null,
          createdAt: x.createdAt?.toDate ? x.createdAt.toDate() : undefined,
        };
      }));
    },
    (err) => {
      console.warn('[blocks] subscribe failed:', err);
      callback([]);
    },
  );
};

/** Live set of user ids the given user has blocked. Empty set when signed out. */
export const useBlockedUsers = (uid: string | undefined | null): Set<string> => {
  const [blocked, setBlocked] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    if (!uid) {
      setBlocked(new Set());
      return;
    }
    return subscribeToBlocks(uid, (list) => setBlocked(new Set(list.map((b) => b.targetUserId))));
  }, [uid]);
  return blocked;
};

export const blockUser = (targetUserId: string) => gameApiService.blockUser(targetUserId);
export const unblockUser = (targetUserId: string) => gameApiService.unblockUser(targetUserId);

const blockService = { subscribeToBlocks, useBlockedUsers, blockUser, unblockUser };
export default blockService;
